import { enumValue } from '~/util';

function getRatioMinMax(
    detail: MeasureDetailType,
    rangeName: 'norm' | 'abnorm',
    whichRatio?: string
) {
    const splitIdx = whichRatio === 'ratio' ? 0 : 1;
    const min = parseFloat(detail[`${rangeName}_min`]!.split('$')[splitIdx]);
    const max = parseFloat(detail[`${rangeName}_max`]!.split('$')[splitIdx]);
    return { min, max };
}

function getNormAbnormValues(detail: MeasureDetailType, whichRatio?: string) {
    if (detail.data_type === enumValue(['gap', 'supplemental_value_data_types', 'Ratio'])) {
        return {
            norm: getRatioMinMax(detail, 'norm', whichRatio),
            abnorm: getRatioMinMax(detail, 'abnorm', whichRatio),
        };
    }
    const result: {
        norm: { min: string | number; max: string | number };
        abnorm: { min: string | number; max: string | number };
    } = {
        norm: { min: parseFloat(detail.norm_min!), max: parseFloat(detail.norm_max!) },
        abnorm: { min: parseFloat(detail.abnorm_min!), max: parseFloat(detail.abnorm_max!) },
    };
    return result;
}

export function getNormAbnormRange(detail: MeasureDetailType, whichRatio?: string) {
    const { norm, abnorm } = getNormAbnormValues(detail, whichRatio);

    // Handle NaN
    const infinity = '∞';
    const negInfinity = `-${infinity}`;
    if (Number.isNaN(norm.min)) norm.min = negInfinity;
    if (Number.isNaN(norm.max)) norm.max = infinity;
    if (Number.isNaN(abnorm.min)) abnorm.min = negInfinity;
    if (Number.isNaN(abnorm.max)) abnorm.max = infinity;

    const normRange = `${norm.min}-${norm.max}`;
    let abnormRange;

    // increment/decrement abnormal bounds by 1 or 0.01 depending on data type
    const changeValue =
        detail.data_type === enumValue(['gap', 'supplemental_value_data_types', 'Integer'])
            ? 1
            : 0.01;

    // Check for intersecting ranges
    if (
        (abnorm.min === negInfinity || abnorm.min < norm.min) &&
        (abnorm.max === infinity || abnorm.max > norm.max)
    ) {
        abnormRange = `${abnorm.min}-${(norm.min as number) - changeValue}, ${
            (norm.max as number) + changeValue
        }-${abnorm.max}`;
    } else if (abnorm.min === norm.min && abnorm.max > norm.max) {
        abnormRange = `${(norm.max as number) + changeValue}-${abnorm.max}`;
    } else if (abnorm.max === norm.max && abnorm.min < norm.min) {
        abnormRange = `${abnorm.min}-${(norm.min as number) - changeValue}`;
    } else {
        abnormRange = `${abnorm.min}-${abnorm.max}`;
    }

    return { normRange, abnormRange };
}

function isRangeValue(
    value: string,
    detail: MeasureDetailType,
    rangeName: 'norm' | 'abnorm',
    whichRatio?: string
) {
    const valueNum = parseFloat(value);
    if (Number.isNaN(valueNum)) return false;
    const range = getNormAbnormValues(detail, whichRatio)[rangeName];
    return (
        (Number.isNaN(range.min) || (range.min as number) <= valueNum) &&
        (Number.isNaN(range.max) || valueNum <= (range.max as number))
    );
}

export function isNormalValue(value: string, detail: MeasureDetailType, ratio?: string) {
    return isRangeValue(value, detail, 'norm', ratio);
}

export function isAbnormalValue(value: string, detail: MeasureDetailType, ratio?: string) {
    if (isNormalValue(value, detail, ratio)) return false;
    return isRangeValue(value, detail, 'abnorm', ratio);
}
